'use client';

import * as React from 'react';
import { MoreHorizontal, PanelLeft, Plus, Search, Trash2 } from 'lucide-react';
import { Button, cn } from '@langbridge-builder/design-system';

export interface SidebarSessionItem {
  id: string;
  title: string;
}

export interface SidebarSessionGroup {
  label: string;
  items: SidebarSessionItem[];
}

/** Static sample rail used by design previews when no real `groups` are passed. */
const MOCK_GROUPS: SidebarSessionGroup[] = [
  {
    label: '오늘',
    items: [
      { id: 's1', title: '위젯을 사이트에 설치하려면?' },
      { id: 's2', title: '요금제 비교' },
    ],
  },
  {
    label: '지난 7일',
    items: [
      { id: 's3', title: 'LangGraph 노드 연결 오류' },
      { id: 's4', title: '커스텀 도메인 연결' },
      { id: 's5', title: '다크모드 토큰 질문' },
    ],
  },
  {
    label: '이전',
    items: [
      { id: 's6', title: '첫 봇 만들기' },
      { id: 's7', title: 'Pretendard 폰트 적용' },
    ],
  },
];

export interface SessionSidebarProps {
  activeId?: string;
  onSelect?: (id: string) => void;
  onNewChat?: () => void;
  onCollapse?: () => void;
  /** Real session groups. When omitted, a static mock rail renders (design previews). */
  groups?: SidebarSessionGroup[];
  /** Delete a session from the rail (hover trash button). */
  onDelete?: (id: string) => void;
  /** Show a loading placeholder instead of the list. */
  loading?: boolean;
}

/**
 * S2 web-app session/history rail. Renders real `groups` when provided (desktop
 * app), else a static mock rail (design previews). Collapse is owned by the shell.
 */
export function SessionSidebar({
  activeId,
  onSelect,
  onNewChat,
  onCollapse,
  groups,
  onDelete,
  loading,
}: SessionSidebarProps) {
  const data = groups ?? MOCK_GROUPS;
  const isEmpty = !loading && data.every((g) => g.items.length === 0);
  return (
    <aside
      className="flex shrink-0 flex-col border-r border-border bg-surface-2"
      style={{ width: 'var(--chat-sidebar-width)' }}
    >
      <div className="flex flex-col gap-2 p-3">
        <div className="flex items-center justify-between px-1 pb-1">
          <div className="flex items-center gap-2">
            <span className="flex size-6 items-center justify-center rounded-md bg-accent text-xs font-extrabold text-accent-fg">
              L
            </span>
            <span className="text-md font-bold text-fg">LangBridge</span>
          </div>
          <button
            type="button"
            onClick={onCollapse}
            aria-label="사이드바 접기"
            className="flex p-1 text-fg-muted hover:text-fg"
          >
            <PanelLeft size={17} />
          </button>
        </div>
        <Button variant="secondary" block size="md" iconLeft={<Plus size={16} />} onClick={onNewChat}>
          새 대화
        </Button>
        <div className="flex h-[34px] items-center gap-2 rounded-control bg-muted px-3 text-fg-muted">
          <Search size={15} />
          <span className="text-sm">대화 검색</span>
        </div>
      </div>

      <nav aria-label="대화 내역" className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {loading ? (
          <p className="px-3 py-4 text-sm text-fg-muted">대화 내역 불러오는 중…</p>
        ) : isEmpty ? (
          <p className="px-3 py-4 text-sm text-fg-muted">아직 대화가 없어요.</p>
        ) : (
          data.map((g) =>
            g.items.length === 0 ? null : (
              <div key={g.label} className="mb-3">
                <div className="px-2 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wider text-fg-muted">
                  {g.label}
                </div>
                <ul role="list" className="flex flex-col">
                  {g.items.map((s) => {
                    const on = s.id === activeId;
                    return (
                      <li key={s.id} className="group/item relative">
                        <button
                          type="button"
                          onClick={() => onSelect?.(s.id)}
                          aria-current={on ? 'page' : undefined}
                          style={{ height: 'var(--session-item-height)' }}
                          className={cn(
                            'flex w-full items-center rounded-[var(--session-item-radius)] px-2 text-left text-sm',
                            onDelete ? 'pr-8' : '',
                            on
                              ? 'bg-accent-soft font-semibold text-accent'
                              : 'font-medium text-fg-secondary hover:bg-hover',
                          )}
                        >
                          <span className="truncate">{s.title}</span>
                        </button>
                        {onDelete ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(s.id);
                            }}
                            aria-label="대화 삭제"
                            className={cn(
                              'absolute right-1.5 top-1/2 flex -translate-y-1/2 rounded-md p-1 text-fg-muted',
                              'opacity-0 hover:bg-hover hover:text-danger group-hover/item:opacity-100',
                              'focus-visible:opacity-100',
                            )}
                          >
                            <Trash2 size={14} />
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ),
          )
        )}
      </nav>

      <div className="flex items-center gap-2 border-t border-border px-3 py-[var(--space-2-5)]">
        <span className="flex size-7 items-center justify-center rounded-full bg-inset text-xs font-bold text-fg-secondary">
          김
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-fg">김개발</div>
          <div className="truncate text-2xs text-fg-muted">Pro 워크스페이스</div>
        </div>
        <button type="button" aria-label="계정 메뉴" className="flex p-1 text-fg-muted hover:text-fg">
          <MoreHorizontal size={16} />
        </button>
      </div>
    </aside>
  );
}
